import { useEffect } from 'react';
import { useSession } from '@/contexts/session-context';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';

interface SuperAdminAccessState {
  hasAccess: boolean;
  loading: boolean;
}

export function useSuperAdminAccess(identityKey?: string | null): SuperAdminAccessState {
  const { isLoading, user } = useSession();
  const cacheKey =
    identityKey && identityKey.trim().length > 0
      ? `is_super_admin:${identityKey.toLowerCase()}`
      : null;
  const cachedValue =
    typeof window !== 'undefined' && cacheKey !== null
      ? sessionStorage.getItem(cacheKey)
      : null;

  // Phase quick-260426-x4h Task C — backend now returns 200 with body
  // `{ hasAccess: boolean }` instead of throwing PermissionError on
  // non-admins. We must read the body, not just `res.ok`, since BOTH
  // admin and non-admin responses are 200.
  // TanStack Query replaces the old module-level in-flight dedup map: all
  // mounts of this hook share one request per query key.
  const query = useAdminApiQuery<{ hasAccess?: boolean }>(
    qk.superAdmin('access', cacheKey),
    '/super-admin/access',
    {
      enabled: !isLoading && !!user && cacheKey !== null,
      // Access is stable per session (mirrors the sessionStorage cache).
      staleTime: Infinity,
    },
  );

  // Any error (network or non-2xx) means "no access", never a crash.
  const networkValue = query.isSuccess
    ? query.data?.hasAccess === true
    : query.isError
      ? false
      : null;

  useEffect(() => {
    if (typeof window === 'undefined' || cacheKey === null || networkValue === null) {
      return;
    }
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('is_super_admin:') && key !== cacheKey)
      .forEach((key) => sessionStorage.removeItem(key));
    sessionStorage.setItem(cacheKey, String(networkValue));
  }, [cacheKey, networkValue]);

  const hasAccess = networkValue ?? cachedValue === 'true';
  const loading =
    !isLoading &&
    !!user &&
    cacheKey !== null &&
    networkValue === null &&
    cachedValue === null;

  return { hasAccess, loading };
}
